import { z } from 'zod';

/** Baza API — same-origin (dev: proxy Vite, prod: nginx → backend). */
export const API_BASE = '/api';

export const RuntimeConfigSchema = z.object({
  app: z.object({ name: z.string(), version: z.string() }),
  auth: z.object({
    mode: z.enum(['keycloak', 'dev', 'unconfigured']),
    keycloak: z
      .object({ url: z.string().optional(), realm: z.string().optional(), clientId: z.string().optional() })
      .nullable(),
  }),
  features: z.object({ storage: z.boolean(), sync: z.boolean() }),
});

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

/** Jeden odrzucony element ciała: nazwa pola z DTO + powód. Patrz `api/src/common/bledy-walidacji.ts`. */
export type BladPola = { pole: string; komunikat: string };

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * POLA ODRZUCONE PRZEZ WALIDACJĘ — puste, gdy błąd nie jest błędem walidacji.
     *
     * ⚠️ ISTNIEJE PO TO, ŻEBY FORMULARZ PODŚWIETLIŁ KONKRETNE POLE. Wcześniej serwer oddawał
     * gołe „Validation failed", a ekran pokazywał to jako jeden czerwony pasek pod oknem —
     * przy formularzu z trzydziestoma polami nie dało się z tego wywnioskować, gdzie patrzeć.
     * Nazwy są ścieżkami z DTO (`costCenter`, `machineIds[0]`), więc pasują do nazw pól
     * formularza wszędzie tam, gdzie formularz trzyma się nazw API.
     */
    readonly pola: BladPola[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ODPOWIEDŹ, KTÓREJ EKRAN NIE UMIE PRZECZYTAĆ — OSOBNA KLASA, BO TO NIE JEST ODMOWA SERWERA.
 *
 *  ⚠️⚠️ POWSTAŁA PO AWARII Z 10.09.2026, zmierzonej na małpkomacie: wymiana indeksu w planogramie
 *  wykonała się w bazie klienta (6 wierszy z nowym indeksem, `DATELASTMODIFIED` 11:09:01),
 *  a portal pokazał czerwone „Nie udało się wymienić indeksu". Serwer oddał wtedy `201`
 *  z operacją już zatwierdzoną, tylko o dwa pola węższą, niż wymagał schemat ekranu — i `parse`
 *  poleciał wyjątkiem, który w oknie nie różnił się NICZYM od odmowy zapisu.
 *
 *  ⚠️⚠️ RÓŻNICA JEST ISTOTNA I MA WŁASNĄ KLASĘ: `ApiError` znaczy „serwer odmówił i nic nie
 *  zapisał", a TO znaczy „serwer zrobił swoje, tylko nie umiemy odczytać odpowiedzi". W drugim
 *  przypadku ekran NIE MA PRAWA powiedzieć „nie udało się" — bo nie wie, a operator na taki
 *  komunikat powtarza operację, która jest już wykonana.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export class OdpowiedzNieczytelna extends Error {
  constructor(
    readonly zadanie: string,
    readonly szczegoly: string,
  ) {
    super(`Serwer odpowiedział inaczej, niż ekran oczekuje (${zadanie}): ${szczegoly}`);
    this.name = 'OdpowiedzNieczytelna';
  }
}

/**
 * WALIDACJA ODPOWIEDZI — JEDNO MIEJSCE DLA WSZYSTKICH TRZECH POMOCNIKÓW.
 *
 * ⚠️ `safeParse`, nie `parse`: goły `ZodError` niósł ścieżki pól, ale żadnej informacji o tym,
 * KTÓRE ŻĄDANIE go wywołało — a to pierwsza rzecz, której trzeba przy rozjeździe front↔API.
 */
function czytajOdpowiedz<T>(zadanie: string, data: unknown, schema?: z.ZodType<T>): T {
  if (!schema) return data as T;
  const wynik = schema.safeParse(data);
  if (wynik.success) return wynik.data;
  const braki = wynik.error.issues
    .slice(0, 5)
    .map((i) => `${i.path.join('.') === '' ? '(korzeń)' : i.path.join('.')}: ${i.message}`)
    .join('; ');
  throw new OdpowiedzNieczytelna(zadanie, braki);
}

/** Ciało błędu → `{ message, pola }`. Odporny na odpowiedź, która nie jest JSON-em. */
async function bladZOdpowiedzi(res: Response): Promise<{ message: string | null; pola: BladPola[] }> {
  const body = await res.json().catch(() => null);
  if (body === null || typeof body !== 'object') return { message: null, pola: [] };
  const rec = body as Record<string, unknown>;
  const surowe = Array.isArray(rec.pola) ? rec.pola : [];
  return {
    message: 'message' in rec ? String(rec.message) : null,
    pola: surowe.filter(
      (p): p is BladPola =>
        typeof p === 'object' && p !== null && typeof (p as BladPola).pole === 'string',
    ),
  };
}

/**
 * CZY TO ODPOWIEDŹ STARSZEJ INSTANCJI, a nie awaria.
 *
 * ⚠️ FRONT I API WDRAŻAJĄ SIĘ OSOBNO i to jest zmierzone, nie teoretyczne: 2026-08-17 portal
 * serwował ekran harmonogramów (build 0.3.243) przy API w wersji 0.3.239, więc nowe trasy
 * odpowiadały `404`. Ekran pokazywał wtedy surowe „GET /reports/harmonogramy → 404", co czyta się
 * jak usterka do zgłoszenia — a jedyną potrzebną czynnością było dokończenie wdrożenia.
 *
 * ⚠️ TYLKO DLA TRAS, KTÓRYCH MY DODALIŚMY. `404` na trasie istniejącej od dawna znaczy „nie ma
 * takiego zasobu" i tego komunikatu nie wolno na nią naciągać.
 */
export const czyStarszaInstancja = (e: unknown): boolean => e instanceof ApiError && e.status === 404;

export async function apiGet<T>(path: string, schema?: z.ZodType<T>): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new ApiError(`GET ${path} → ${res.status}`, res.status);
  }
  const data: unknown = await res.json();
  return czytajOdpowiedz(`GET ${path}`, data, schema);
}

export async function apiPost<T>(path: string, body?: unknown, schema?: z.ZodType<T>): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });

  if (!res.ok) {
    // backend zwraca { message, pola } — pokazujemy to użytkownikowi zamiast surowego kodu
    const { message, pola } = await bladZOdpowiedzi(res);
    throw new ApiError(message ?? `POST ${path} → ${res.status}`, res.status, pola);
  }

  const data: unknown = await res.json().catch(() => ({}));
  return czytajOdpowiedz(`POST ${path}`, data, schema);
}

export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  schema?: z.ZodType<T>,
): Promise<T> {
  /**
   * ⚠️ `Content-Type` TYLKO WTEDY, GDY JEST CO WYSŁAĆ.
   *
   * Fastify parsuje ciało na podstawie tego nagłówka i odrzuca żądanie, które go DEKLARUJE,
   * a ciała nie ma: `Body cannot be empty when content-type is set to 'application/json'`.
   * Wcześniej nagłówek szedł zawsze, więc KAŻDE `DELETE` bez ciała kończyło się tym błędem
   * — zgłoszone przy „Odbierz dostęp", ale dotyczyło tak samo usuwania limitu z ekranu
   * Ograniczeń (`rationing-api.ts`) i zdejmowania limitu w Uprawnieniach. Trzy wywołania,
   * jedna przyczyna, więc i poprawka jest jedna: tutaj, a nie doklejanie pustych obiektów
   * w każdym module z osobna.
   */
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    const { message, pola } = await bladZOdpowiedzi(res);
    throw new ApiError(message ?? `${method} ${path} → ${res.status}`, res.status, pola);
  }

  const data: unknown = await res.json().catch(() => ({}));
  return czytajOdpowiedz(`${method} ${path}`, data, schema);
}

export const fetchRuntimeConfig = () => apiGet('/config', RuntimeConfigSchema);

// ---------- auth ----------

export const AppUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(['ADMIN', 'USER']),
  /** NADANIA — co komu zapisano na koncie. Do pokazania na ekranie kont, nie do decyzji. */
  modules: z.array(z.string()),
  /**
   * MODUŁY EFEKTYWNE — co to konto realnie może czytać. Liczy je SERWER.
   *
   * Front nie powtarza reguły „admin ma wszystko": ta reguła ma wyjątek (admin klienta dostaje
   * tylko sufit z §8a) i właśnie na jej powtórzeniu polegał błąd — `ModulesGuard` i `can()`
   * miały ten sam skrót, więc menu pokazywało pozycje, na które API i tak by nie wpuściło.
   * Tu przychodzi WYNIK; front nie wie, dlaczego czegoś na liście nie ma.
   */
  effectiveModules: z.array(z.string()),
  /**
   * ZAWĘŻENIE DO KLIENTA; `null` = konto SDI, widzi wszystkich klientów.
   *
   * Front potrzebuje tego, bo ROLA NIE WYSTARCZA do decyzji o widoczności: admin klienta jest
   * adminem i przechodzi każdą bramkę modułową, a powierzchnie obejmujące CAŁĄ instancję
   * (logi, ślad audytu) są tylko dla SDI. Do niczego nie upoważnia — pilnuje tego
   * `SdiOnlyGuard` po stronie API; tutaj służy wyłącznie do nierysowania pozycji, na której
   * użytkownik i tak dostanie odmowę.
   */
  clientId: z.string().nullable(),
});

export type AppUser = z.infer<typeof AppUserSchema>;

const MeSchema = z.object({ user: AppUserSchema });

/** `null` = brak sesji (401 to normalny stan, nie błąd). */
export async function fetchMe(): Promise<AppUser | null> {
  try {
    return (await apiGet('/auth/me', MeSchema)).user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export const loginRequest = (email: string, password: string) =>
  apiPost('/auth/login', { email, password }, MeSchema).then((r) => r.user);

export const logoutRequest = () => apiPost<unknown>('/auth/logout');
